import React, { useState, useEffect } from "react";
import {
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaLinkedinIn,
  FaFacebookF,
  FaInstagram,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import API from "../../api";
import { FaWhatsapp } from "react-icons/fa";

const Contact = () => {
  // --- LANGUAGE SYNC LOGIC ---
  const [lang, setLang] = useState(localStorage.getItem("lang") || "en");

  useEffect(() => {
    const handleStorageChange = () => {
      setLang(localStorage.getItem("lang") || "en");
    };
    window.addEventListener("storage", handleStorageChange);
    const interval = setInterval(handleStorageChange, 500);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // --- TRANSLATION CONTENT ---
  const t = {
    en: {
      badge: "Get In Touch",
      title1: "We would love to",
      titleHighlight: "hear from you",
      title2: ".",
      desc: "Whether you have a specific need in mind or simply want to understand your options better, the best place to start is a conversation. We are here, we are listening, and we genuinely enjoy talking through this with people.",
      cNum: "Contact Number",
      eSupp: "Email Support",
      sOff: "Singapore Office",
      follow: "Follow Us:",
      fName: "Full Name",
      eAddr: "Email Address",
      org: "Organisation",
      help: "How can we help?",
      msg: "Your Message",
      btn: "Send Message",
      btnLoading: "Processing...",
      pName: "John Doe",
      pEmail: "juanna@rigors.com.sg",
      ph: "Phone Number",
      pPh: "+65 9686 1689",
      pOrg: "Company Name (Optional)",
      pHelp: "Select an option",
      pMsg: "Tell us how we can help...",
      valWarn: "Please fill in all required fields before sending.",
      valSucc: "Message sent successfully! We'll get back to you soon.",
      valErr: "Failed to send message. Please try again later.",
    },
    zh: {
      badge: "联系我们",
      title1: "我们期待",
      titleHighlight: "您的来信",
      title2: "。",
      desc_zh:
        "无论您已有明确需求，还是希望更清晰地了解可选方案，最好的开始方式是一场交流。我们随时在这里，认真倾听，并真诚地与您探讨每一个细节。",
      cNum: "联系电话",
      eSupp: "邮件支持",
      sOff: "新加坡办公室",
      follow: "关注我们:",
      fName: "全名",
      eAddr: "电子邮箱",
      org: "组织/公司",
      help: "我们如何提供帮助？",
      msg: "您的留言",
      btn: "发送消息",
      btnLoading: "处理中...",
      pName: "张三",
      ph: "电话号码",
      pPh: "+65 9686 1689",
      pEmail: "juanna@rigors.com.sg",
      pOrg: "公司名称（可选）",
      pHelp: "请选择一个选项",
      pMsg: "告诉我们如何为您提供帮助...",
      valWarn: "请在发送前填写所有必填字段。",
      valSucc: "消息发送成功！我们将尽快回复您。",
      valErr: "发送消息失败。请稍后再试。",
    },
  };

  const cur = t[lang];

  // Form State
  const [formData, setFormData] = useState({
    fullName: "",
    organisation: "",
    email: "",
    contactNumber: "",
    helpWith: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Input Change Handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  // Validation Logic
  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName) newErrors.fullName = cur.fName === "Full Name" ? "Full name is required" : "全名是必填项";
    if (!formData.email) {
      newErrors.email = cur.eAddr === "Email Address" ? "Email is required" : "电子邮箱是必填项";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = cur.eAddr === "Email Address" ? "Invalid email format" : "电子邮箱格式不正确";
    }
    if (!formData.contactNumber) newErrors.contactNumber = cur.cNum === "Contact Number" ? "Contact number is required" : "联系电话是必填项";
    if (!formData.helpWith) newErrors.helpWith = cur.help === "How can we help?" ? "Please select an option" : "请选择一个选项";
    if (!formData.message) newErrors.message = cur.msg === "Your Message" ? "Message is required" : "留言是必填项";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return toast.warn(cur.valWarn);
    }

    setLoading(true);
    try {
      const response = await API.post("/api/contact", formData);
      if (response.data.success) {
        toast.success(cur.valSucc);
        setFormData({
          fullName: "",
          organisation: "",
          email: "",
          contactNumber: "",
          helpWith: "",
          message: "",
        });
      } else {
        toast.error(cur.valErr);
      }
    } catch (error) {
      console.error("Submission Error:", error);
      toast.error(error.response?.data?.message || "Server error.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="overflow-hidden transition-colors duration-500 bg-brand-bg text-brand-text font-arial">
      <ToastContainer position="top-right" autoClose={5000} theme="colored" />

      <section className="relative py-10 bg-white">
        <div className="container px-6 mx-auto max-w-7xl">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-12 items-start">
            {/* LEFT SIDE */}
            <div className="w-full lg:w-5/12">
              <span className="text-[#B08D57] uppercase tracking-[0.35em] text-[11px] mb-4 block">
                {lang === "en" ? "Contact" : "联系我们"}
              </span>

              <h2 className="text-[28px] md:text-[34px] font-[Playfair_Display] text-[#1A1A1A] leading-tight mb-6">
                {lang === "en"
                  ? "We would love to hear from you"
                  : "我们很乐意听到您的声音"}
              </h2>

              <p className="text-[#999999] text-[14px] md:text-[16px] leading-relaxed mb-10 max-w-md">
                {lang === "en" ? (
                  <>
                    Whether you have a specific need in mind or simply want to
                    understand your options better, the best place to start is a
                    conversation. We are here, we are listening, and we
                    genuinely enjoy talking through this with people.
                    <br />
                    <br />
                    Reach out in whichever way suits you best. We will get back
                    to you promptly and personally.
                  </>
                ) : (
                  <>
                    无论您是否已经有明确需求，或只是希望更好地了解您的选择，最好的开始方式就是与我们交流。我们在这里，认真倾听，并且真诚地享受与每一位客户的沟通。
                    <br />
                    <br />
                    您可以选择任何适合您的方式与我们联系，我们会及时并亲自回复您。
                  </>
                )}
              </p>

              {/* CONTACT DETAILS */}
              <div className="space-y-6">
                {/* PHONE */}
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#F5F0E8] flex items-center justify-center text-[#B08D57] shrink-0">
                    <i className="fa-solid fa-phone text-[13px]"></i>
                  </div>
                  <p className="text-[#1A1A1A] text-[14px] leading-relaxed">
                    +65 9686 1689
                  </p>
                </div>

                {/* EMAIL */}
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#F5F0E8] flex items-center justify-center text-[#B08D57] shrink-0">
                    <i className="fa-solid fa-envelope text-[13px]"></i>
                  </div>
                  <p className="text-[#1A1A1A] text-[14px] leading-relaxed">
                    juanna@rigors.com.sg
                  </p>
                </div>

                {/* ADDRESS */}
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#F5F0E8] flex items-center justify-center text-[#B08D57] shrink-0">
                    <i className="fa-solid fa-location-dot text-[13px]"></i>
                  </div>
                  <p className="text-[#1A1A1A] text-[14px] leading-relaxed">
                    {lang === "en" ? (
                      <>
                        371 Alexandra Road, #05-11 <br />
                        Singapore 159963 <br />
                        Monday to Friday, 9.00am to 6.00pm
                      </>
                    ) : (
                      <>
                        新加坡亚历山德拉路371号 #05-11 <br />
                        邮编 159963 <br />
                        周一至周五，上午9点至下午6点
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* RIGHT SIDE: FORM */}
            <div className="w-full lg:w-6/12">
              <div className="bg-[#F5F0E8] border border-[#E5E0D8] p-8 md:p-10">
                <h3 className="text-[18px] font-semibold text-[#1A1A1A] mb-6">
                  {lang === "en" ? "Or leave us a message" : "或给我们留言"}
                </h3>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="relative">
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        placeholder="Full Name"
                        className={`w-full bg-white border px-5 py-4 text-sm focus:outline-none ${errors.fullName ? "border-red-500" : "border-[#E5E0D8]"} focus:border-[#B08D57]`}
                      />
                      {errors.fullName && <span className="text-red-500 text-[11px] absolute -bottom-5 left-1">{errors.fullName}</span>}
                    </div>

                    <div className="relative">
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Email Address"
                        className={`w-full bg-white border px-5 py-4 text-sm focus:outline-none ${errors.email ? "border-red-500" : "border-[#E5E0D8]"} focus:border-[#B08D57]`}
                      />
                      {errors.email && <span className="text-red-500 text-[11px] absolute -bottom-5 left-1">{errors.email}</span>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="relative">
                      <input
                        type="tel"
                        name="contactNumber"
                        value={formData.contactNumber}
                        onChange={handleChange}
                        placeholder="Contact Number"
                        className={`w-full bg-white border px-5 py-4 text-sm focus:outline-none ${errors.contactNumber ? "border-red-500" : "border-[#E5E0D8]"} focus:border-[#B08D57]`}
                      />
                      {errors.contactNumber && <span className="text-red-500 text-[11px] absolute -bottom-5 left-1">{errors.contactNumber}</span>}
                    </div>

                    <input
                      type="text"
                      name="organisation"
                      value={formData.organisation}
                      onChange={handleChange}
                      placeholder="Organisation (optional)"
                      className="w-full bg-white border border-[#E5E0D8] px-5 py-4 text-sm focus:outline-none focus:border-[#B08D57]"
                    />
                  </div>

                  <div className="relative">
                    <select
                      name="helpWith"
                      value={formData.helpWith}
                      onChange={handleChange}
                      className={`w-full bg-white border px-5 py-4 text-sm focus:outline-none ${errors.helpWith ? "border-red-500" : "border-[#E5E0D8]"} focus:border-[#B08D57]`}
                    >
                      <option value="">What can we help you with?</option>
                      <option>Business Insurance</option>
                      <option>Personal & General Insurance</option>
                      <option>Employee Benefits</option>
                      <option>Marine & Specialty</option>
                      <option>Life & Health</option>
                      <option>Investment Insurance</option>
                      <option>Careers</option>
                      <option>Other</option>
                    </select>
                    {errors.helpWith && <span className="text-red-500 text-[11px] absolute -bottom-5 left-1">{errors.helpWith}</span>}
                  </div>

                  <div className="relative">
                    <textarea
                      rows="5"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Message"
                      className={`w-full bg-white border px-5 py-4 text-sm focus:outline-none ${errors.message ? "border-red-500" : "border-[#E5E0D8]"} focus:border-[#B08D57] resize-none`}
                    ></textarea>
                    {errors.message && <span className="text-red-500 text-[11px] absolute -bottom-5 left-1">{errors.message}</span>}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full text-[13px] uppercase tracking-[0.3em] font-semibold text-[#1A1A1A] hover:text-[#B08D57] transition"
                  >
                    {loading ? "Sending..." : "Send →"}
                  </button>
                </form>

                <p className="mt-6 text-[12px] text-[#999999]">
                  {lang === "en"
                    ? "We typically respond within one business day. Everything you share with us is treated in complete confidence."
                    : "我们通常会在一个工作日内回复。您所提供的所有信息都会被严格保密。"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-10 bg-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          {/* WHITE BOX */}
          <div className="bg-white border border-[#999999]/20 p-8 md:p-10 mx-auto max-w-6xl">
            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              {/* LEFT CONTENT */}
              <div className="md:w-2/3">
                {/* TITLE */}
                <h2 className="text-[28px] md:text-[34px] font-[Playfair_Display] text-[#1A1A1A] leading-tight mb-4">
                  {lang === "en" ? "Let's talk" : "让我们聊聊"}
                </h2>

                {/* DESCRIPTION */}
                <p className="text-[#999999] text-[14px] md:text-[16px] leading-relaxed max-w-xl">
                  {lang === "en"
                    ? "The fastest way to reach us is on WhatsApp. Drop us a message and we will arrange a time to connect at your convenience."
                    : "通过 WhatsApp 联系我们是最快的方式。发送消息给我们，我们将根据您的时间安排与您沟通。"}
                </p>
              </div>

              {/* RIGHT CTA */}
              <div className="flex justify-start md:justify-end md:w-1/3 mt-4 md:mt-0">
                <a
                  href="https://wa.me/6596861689"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#1A1A1A] text-[14px] font-semibold tracking-wide hover:text-[#B08D57] transition group"
                >
                  {/* WhatsApp Icon */}
                  <i className="fa-brands fa-whatsapp text-[#25D366] text-[36px]"></i>

                  {/* TEXT */}
                  {lang === "en"
                    ? "Message Us on WhatsApp"
                    : "通过 WhatsApp 联系我们"}

                  {/* ARROW */}
                  <span className="transition-transform duration-300 group-hover:translate-x-1">
                    →
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAP SECTION WRAPPED IN WHITE BACKGROUND */}
      <div className="bg-white pt-5 w-full">
        <section className="w-full h-[450px] transition-all duration-700">
          <iframe
            title="Singapore Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.82476595679!2d103.80517597587883!3d1.278640161803738!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31da1bb765355609%3A0xc3f982a5c517740e!2s371%20Alexandra%20Rd%2C%20Singapore%20159963!5e0!3m2!1sen!2ssg!4v1709123456789!5m2!1sen!2ssg"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            className="shadow-inner"
          ></iframe>
        </section>
      </div>
    </div>
  );
};

export default Contact;